import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useMyPermissions from "../hooks/useMyPermissions";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import { getPermissionMatrix } from "../api/salonPermissions";

const OWNER_ONLY_PERMISSIONS = ["settings.manage", "users.manage_roles"];

const ROLE_HEADERS = [
  { value: "owner", label: "Owner" },
  { value: "manager", label: "Manager" },
  { value: "receptionist", label: "Receptionist" },
  { value: "independent_professional", label: "Independent Pro" },
  { value: "technician", label: "Technician" },
  { value: "apprentice", label: "Apprentice" },
];

const PERMISSION_GROUPS = [
  {
    title: "Scheduling",
    items: [
      {
        key: "schedule.view_all",
        label: "See the full schedule",
        tooltip: "Lets this person see everyone's appointments on the salon schedule.",
      },
      {
        key: "schedule.view_own",
        label: "See your own schedule",
        tooltip: "Lets this person see only the appointments assigned to them.",
      },
      {
        key: "schedule.edit",
        label: "Create, edit, or cancel appointments",
        tooltip: "Lets this person add appointments, change appointments, and cancel appointments.",
      },
      {
        key: "schedule.cancel",
        label: "Show cancelled appointments",
        tooltip: "Lets this person turn on Show Cancelled and see cancelled appointments on the schedule.",
      },
      {
        key: "appointments.manage_options",
        label: "Appointment options",
        tooltip:
          "Lets this person use appointment pop-up buttons like check in, confirm, left message, edit, cancel, reschedule, and recurring.",
      },
    ],
  },
  {
    title: "Time Clock",
    items: [
      {
        key: "timeclock.view",
        label: "View and stamp time clocks",
        tooltip: "Lets this person see time clock records and clock staff in or out.",
      },
      {
        key: "timeclock.edit",
        label: "Edit time clock entries",
        tooltip: "Lets this person fix times or notes on time clock entries.",
      },
      {
        key: "timeclock.delete",
        label: "Delete time clock entries",
        tooltip: "Lets this person remove time clock entries that should not be kept.",
      },
    ],
  },
  {
    title: "Clients",
    items: [
      {
        key: "clients.manage",
        label: "Manage client records",
        tooltip: "Lets this person add clients and update client records.",
      },
      {
        key: "clients.view_contact",
        label: "View client contact info",
        tooltip: "Lets this person see client phone numbers, emails, and other contact details.",
      },
      {
        key: "clients.view_metrics",
        label: "View client visit and spend metrics",
        tooltip: "Lets this person see client visit history and spending totals.",
      },
      {
        key: "client_portal.manage",
        label: "Manage client portal and online booking",
        tooltip:
          "Lets this person manage online booking, client portal settings, and client portal requests.",
      },
    ],
  },
  {
    title: "Marketing",
    items: [
      {
        key: "marketing.manage",
        label: "Use marketing tools",
        tooltip:
          "Lets this person use the Marketing menu, including rewards, client feedback, campaigns, email tools, appointment emails, and duplicate email tools.",
      },
    ],
  },
  {
    title: "Emails",
    items: [
      {
        key: "emails.manage_opt_outs",
        label: "Manage email opt-outs and preferences",
        tooltip: "Lets this person update a client's email opt-out choices and message preferences.",
      },
    ],
  },
  {
    title: "Tickets",
    items: [
      {
        key: "tickets.manage",
        label: "Manage tickets",
        tooltip: "Lets this person create, edit, and close tickets for checkout.",
      },
      {
        key: "tickets.delete",
        label: "Delete tickets",
        tooltip: "Lets this person delete tickets that should not stay in sales history.",
      },
      {
        key: "tickets.reopen",
        label: "Re-open tickets",
        tooltip: "Lets this person reopen closed tickets when a checkout needs a correction.",
      },
      {
        key: "tickets.refund",
        label: "Refund tickets",
        tooltip: "Lets this person issue refunds from tickets.",
      },
      {
        key: "gift_cards.manage",
        label: "Manage gift cards",
        tooltip: "Lets this person sell, adjust, and redeem gift cards.",
      },
    ],
  },
  {
    title: "Services",
    items: [
      {
        key: "services.manage",
        label: "Manage services",
        tooltip: "Lets this person add and update services, pricing, categories, and online booking options.",
      },
    ],
  },
  {
    title: "Operations",
    items: [
      {
        key: "maintenance.manage",
        label: "Maintenance & Cleaning",
        tooltip:
          "Lets this person open Maintenance & Cleaning to manage salon cleaning tasks and check maintenance reports.",
      },
      {
        key: "inventory.manage",
        label: "Inventory",
        tooltip: "Lets this person open Inventory to add products, update stock counts, and track low supplies.",
      },
      {
        key: "cash_drawer.manage",
        label: "Cash drawer",
        tooltip: "Lets this person open the Cash Drawer page to enter and count drawer cash.",
      },
      {
        key: "walkin_queue.manage",
        label: "Walk-in queue slider",
        tooltip: "Lets this person use the walk-in queue slider on the schedule.",
      },
    ],
  },
  {
    title: "Workspace Rentals",
    items: [
      {
        key: "rentals.view",
        label: "View workspace rentals",
        tooltip: "Lets this person see workspace rental listings and booking details.",
      },
      {
        key: "rentals.manage",
        label: "Manage workspace rentals",
        tooltip: "Lets this person create and update workspace rentals.",
      },
      {
        key: "rentals.marketplace_manage",
        label: "Manage marketplace listings",
        tooltip: "Lets this person publish and update rental listings shown on the marketplace.",
      },
      {
        key: "rentals.reports.view",
        label: "View rental reports",
        tooltip: "Lets this person see reports for workspace rental activity.",
      },
    ],
  },
  {
    title: "Staff",
    items: [
      {
        key: "staff.manage",
        label: "Manage staff members",
        tooltip: "Lets this person open staff management tools and staff setup pages.",
      },
      {
        key: "staff.update",
        label: "Update Staff",
        tooltip: "Lets this person save changes to staff information, login details, photos, and schedules.",
      },
      {
        key: "staff.uses_time_clock",
        label: "Uses Time Clock",
        tooltip: "Lets this person turn the Uses Time Clock setting on or off for staff.",
      },
      {
        key: "staff.show_online_booking",
        label: "Show in Online Booking",
        tooltip: "Lets this person choose whether a staff member appears for online booking.",
      },
    ],
  },
  {
    title: "Platform Access",
    items: [
      {
        key: "platform.myboothshare_access",
        label: "Access Ellyanna Boothshare",
        tooltip: "Shows Ellyanna Boothshare links for this person so they can open workspace rental tools.",
      },
      {
        key: "platform.salonbusinesscpa_access",
        label: "Access Ellyanna Accounting",
        tooltip: "Shows Ellyanna Accounting links for this person so they can open accounting support tools.",
      },
    ],
  },
  {
    title: "Settings",
    items: OWNER_ONLY_PERMISSIONS.map((key) => ({
      key,
      label: key === "settings.manage" ? "Manage salon settings" : "Manage user roles",
      tooltip:
        key === "settings.manage"
          ? "Lets this person change salon setup, business rules, and system settings."
          : "Lets this person add users, change roles, and adjust permission access.",
    })),
  },
  {
    title: "Rewards",
    items: [
      {
        key: "rewards.view",
        label: "View rewards program",
        tooltip: "Lets this person see the salon rewards program and reward activity.",
      },
    ],
  },
  {
    title: "Reports",
    items: [
      {
        key: "reports.view",
        label: "View reports",
        tooltip: "Lets this person open salon reports and view report results.",
      },
      {
        key: "schedule.estimated_revenue",
        label: "View estimated revenue",
        tooltip: "Lets this person see estimated money totals on the schedule.",
      },
    ],
  },
  {
    title: "Payroll",
    items: [
      {
        key: "payroll.view",
        label: "View payroll",
        tooltip: "Lets this person see payroll information.",
      },
      {
        key: "payroll.manage",
        label: "Manage payroll and pay stubs",
        tooltip: "Lets this person run payroll tools and manage pay stubs.",
      },
    ],
  },
  {
    title: "Training & Development",
    items: [
      {
        key: "training.view",
        label: "View assigned training",
        tooltip: "Lets this person see training assigned to them.",
      },
      {
        key: "training.manage",
        label: "Create modules and assign training",
        tooltip: "Lets this person create training, assign it to staff, and review progress.",
      },
    ],
  },
];

const formatBool = (value) => (value ? "Yes" : "No");

export default function ManageUsersRoles() {
  const navigate = useNavigate();
  const { token, role, user } = useAuth();
  const normalizedRole = (role || "").toLowerCase();
  const { permissions: routePermissions, loading: routePermissionsLoading } =
    useMyPermissions({ enabled: Boolean(token) });
  const canAccessUserRoles =
    ["admin", "host"].includes(normalizedRole) ||
    Boolean(user?.is_super_admin) ||
    (routePermissions === null
      ? ["admin", "host", "owner", "manager", "solo_technician", "reception"].includes(
          normalizedRole
        )
      : Boolean(routePermissions?.["users.manage_roles"]));

  const [permissionMatrix, setPermissionMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(true);
  const [matrixError, setMatrixError] = useState("");

  useEffect(() => {
    if (!token) {
      navigate("/login?next=/manager/manage/users", { replace: true });
      return;
    }
    if (routePermissionsLoading || routePermissions === null) {
      return;
    }
    if (!canAccessUserRoles) {
      navigate("/my-bookings", { replace: true });
    }
  }, [
    token,
    navigate,
    routePermissionsLoading,
    routePermissions,
    canAccessUserRoles,
  ]);

  useEffect(() => {
    const loadMatrix = async () => {
      if (!token) {
        setMatrixLoading(false);
        return;
      }
      setMatrixLoading(true);
      setMatrixError("");
      try {
        const data = await getPermissionMatrix(token);
        setPermissionMatrix(data);
      } catch (err) {
        setMatrixError("Unable to load permission matrix");
        setPermissionMatrix(null);
      } finally {
        setMatrixLoading(false);
      }
    };
    loadMatrix();
  }, [token]);

  const sectionCard = "rounded-2xl border border-slate-200 bg-white";
  const sectionHeader =
    "px-5 py-4 border-b border-slate-100 flex items-center justify-between";
  const tableHeadClass =
    "bg-slate-50 text-slate-500 uppercase text-xs tracking-wide";
  const matrixTableStyle = {
    fontSize: "clamp(13px, 0.95vw, 15px)",
    lineHeight: 1.25,
  };
  const matrixHeadStyle = {
    fontSize: "clamp(12px, 0.9vw, 14px)",
    lineHeight: 1.25,
  };
  const permissionColWidth = "40%";
  const roleColWidth = `${(60 / ROLE_HEADERS.length).toFixed(2)}%`;
  const matrixCellClass = "px-3 py-2.5 md:px-4 md:py-3";

  const renderPermissionName = (
    item,
    { placement = "top", labelClassName = "truncate" } = {}
  ) => (
    <span className="inline-flex min-w-0 items-center gap-2 align-middle">
      <span className={labelClassName} title={item.label}>
        {item.label}
      </span>
      {item.tooltip && (
        <InfoTooltip
          text={item.tooltip}
          label={item.label}
          placement={placement}
          maxWidth={300}
          className="shrink-0"
        />
      )}
    </span>
  );

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="manageSectionShell">
      <div className="manageSectionWide manage-section-wide manage-section-compact">
      <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
        <div className="bg-rose-500 px-6 py-5 relative">
          <h1 className="text-2xl font-semibold text-white">Users &amp; Roles</h1>
          <p className="text-white/90 mt-1">
            Manage access and permissions
          </p>
          <div
            className="absolute right-4 top-3 z-50"
            onClick={handleClose}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <ModalCloseX onClick={handleClose} />
          </div>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div className={sectionCard}>
            <div className={sectionHeader}>
              <div className="text-lg font-semibold text-slate-900">
                Role Permission Matrix
              </div>
              <span className="text-xs text-slate-500">Defaults by role</span>
            </div>
            <div className="px-5 py-4">
              {matrixLoading ? (
                <p className="text-sm text-slate-500">Loading permission matrix...</p>
              ) : matrixError ? (
                <p className="text-sm text-rose-600">{matrixError}</p>
              ) : permissionMatrix ? (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="w-full table-fixed" style={matrixTableStyle}>
                    <colgroup>
                      <col style={{ width: permissionColWidth }} />
                      {ROLE_HEADERS.map((header) => (
                        <col key={`${header.value}-col`} style={{ width: roleColWidth }} />
                      ))}
                    </colgroup>
                    <thead className={tableHeadClass} style={matrixHeadStyle}>
                      <tr>
                        <th className={`${matrixCellClass} text-left align-middle`}>
                          Permission
                        </th>
                        {ROLE_HEADERS.map((header) => (
                          <th
                            key={header.value}
                            className={`${matrixCellClass} text-center align-middle whitespace-nowrap`}
                          >
                            {header.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {PERMISSION_GROUPS.map((group) => (
                        <Fragment key={group.title}>
                          <tr>
                            <td
                              colSpan={ROLE_HEADERS.length + 1}
                              className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500 bg-slate-50"
                            >
                              {group.title}
                            </td>
                          </tr>
                          {group.items.map((item) => (
                            <tr key={item.key} className="border-b border-slate-100">
                              <td className={`${matrixCellClass} text-left align-middle`}>
                                {renderPermissionName(item)}
                              </td>
                              {ROLE_HEADERS.map((header) => {
                                const value = item.matrixOnly
                                  ? item.roleDefaults?.[header.value]
                                  : permissionMatrix.role_defaults?.[header.value]?.[item.key];
                                const valueClass = value
                                  ? "text-slate-900 font-semibold"
                                  : "text-slate-400";
                                return (
                                  <td
                                    key={`${item.key}-${header.value}`}
                                    className={`${matrixCellClass} text-center align-middle whitespace-nowrap`}
                                  >
                                    <span
                                      className={`${valueClass} inline-block w-full text-center`}
                                    >
                                      {formatBool(value)}
                                    </span>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Permission data unavailable.</p>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
